<template>
  <footer
    class="site-footer site-footer-theme home-section home-section--accent-top"
    :class="themeClass"
    aria-labelledby="site-footer-heading"
  >
    <UiHomeSectionDecor variant="ecosystem" />
    <div class="home-news__ambient" aria-hidden="true" />

    <div class="site-footer__inner">
      <div class="site-footer__stage">
        <div class="site-footer__brand-divider" aria-hidden="true">
          <span class="site-footer__brand-line" />
          <GcsaLogo
            class="site-footer__brand-mark"
            :show-wordmark="false"
            :show-tagline="false"
            :theme="theme"
            size="sm"
          />
          <span class="site-footer__brand-line" />
        </div>

        <section class="site-footer__contact-zone">
          <h2 id="site-footer-heading" class="site-footer__contact-heading">
            {{ resolved.title }}
          </h2>

          <div class="site-footer__contact-panel">
            <div class="site-footer__email-shell">
              <a :href="`mailto:${resolved.email}`" class="site-footer__email-core">
                <span class="site-footer__email-icon" aria-hidden="true">
                  <svg
                    class="site-footer__email-svg"
                    width="23"
                    height="19"
                    viewBox="0 0 23 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21.2305 0C22.2076 0 23 0.792412 23 1.76953V16.8076C23 17.7847 22.2076 18.5771 21.2305 18.5771H1.76953C0.792412 18.5771 0 17.7847 0 16.8076V1.76953C0 0.792412 0.792412 0 1.76953 0H21.2305ZM11.2959 8.7373L5.62305 4.21777L4.37695 5.78223L10.6523 10.7822L11.2549 11.2617L11.873 10.8027L18.5967 5.80273L17.4033 4.19727L11.2959 8.7373Z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
                <span class="site-footer__email-copy min-w-0">
                  <span class="site-footer__email-label">{{ resolved.emailLabel }}</span>
                  <span class="site-footer__email-value">{{ resolved.email }}</span>
                </span>
                <span class="site-footer__email-arrow-shell" aria-hidden="true">
                  <svg
                    class="site-footer__email-arrow-svg"
                    width="13"
                    height="13"
                    viewBox="0 0 13 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3.36914 1H11.1914V8.82222"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="square"
                    />
                    <path
                      d="M10.703 1.48901L1.41406 10.7779"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="square"
                    />
                  </svg>
                </span>
              </a>
            </div>

            <div class="site-footer__social-stack">
              <SocialIconLinks
                :links="resolved.socialLinks"
                :aria-label="resolved.socialAriaLabel"
                :pending-label="resolved.socialPendingLabel"
              />
            </div>
          </div>
        </section>

        <section class="site-footer__nav-zone" :aria-label="resolved.navAriaLabel">
          <nav class="site-footer__nav">
            <ul class="site-footer__nav-list">
              <li
                v-for="link in resolved.navLinks"
                :key="link.key"
                class="site-footer__nav-item"
              >
                <a
                  :href="link.href"
                  class="site-footer__nav-link"
                  :target="link.external ? '_blank' : undefined"
                  :rel="link.external ? 'noopener noreferrer' : undefined"
                >
                  <UiNavIcon :name="link.icon" />
                  <span class="site-footer__nav-label">{{ link.label }}</span>
                  <svg
                    class="site-footer__nav-arrow"
                    width="8"
                    height="9"
                    viewBox="0 0 8 9"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M3.31529 1.06055L6.63058 4.37584L3.31529 7.69113"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="square"
                    />
                  </svg>
                </a>
              </li>
            </ul>
          </nav>
        </section>

        <div class="site-footer__counsel-block">
          <h2 class="site-footer__counsel-heading">
            {{ counselCopy.title }}
          </h2>
          <nav class="site-footer__counsel-row" :aria-label="counselCopy.title">
            <button
              v-for="firm in counselCopy.firms"
              :key="firm.id"
              type="button"
              class="site-footer__counsel-item"
              :title="counselButtonLabel(firm)"
              :aria-label="counselButtonLabel(firm)"
              @click="activeCounsel = firm"
            >
              <span class="site-footer__counsel-logo-wrap" aria-hidden="true">
                <FooterCounselLogo
                  class="site-footer__counsel-logo"
                  :firm-id="firm.id"
                />
              </span>
              <span class="site-footer__counsel-name">
                <span class="site-footer__counsel-text">{{ firm.title }}</span>
                <span class="site-footer__counsel-hint" aria-hidden="true">
                  <svg
                    viewBox="0 0 8 9"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3.31529 1.06055L6.63058 4.37584L3.31529 7.69113"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="square"
                    />
                  </svg>
                </span>
              </span>
            </button>
          </nav>
        </div>
      </div>

      <div class="site-footer__bottom">
        <p>{{ resolved.copyright }}</p>
        <nav class="site-footer__legal" :aria-label="resolved.legalAriaLabel">
          <a
            v-for="(item, index) in resolved.legalLinks"
            :key="`${item.label}-${index}`"
            :href="item.href"
            :target="item.external ? '_blank' : undefined"
            :rel="item.external ? 'noopener noreferrer' : undefined"
          >
            {{ item.label }}
          </a>
        </nav>
      </div>
    </div>

    <FooterCounselModal
      :firm="activeCounsel"
      :copy="counselCopy"
      @close="activeCounsel = null"
    />
  </footer>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import FooterCounselLogo from './FooterCounselLogo.vue';
import FooterCounselModal from './FooterCounselModal.vue';
import GcsaLogo from './GcsaLogo.vue';
import UiHomeSectionDecor from './UiHomeSectionDecor.vue';
import SocialIconLinks from './SocialIconLinks.vue';
import UiNavIcon from './UiNavIcon.vue';
import {
  resolveGcsaFooter,
  type GcsaFooterSnapshot,
  type SiteFooterLocale
} from '../consts/gcsaFooterContent';
import {
  resolveGcsaLegalCounsel,
  type GcsaLegalCounselFirm
} from '../consts/gcsaLegalCounselContent';

const props = withDefaults(
  defineProps<{
    /**
     * 语言。默认 zh-CN，与官网中文页脚完全一致。
     * 也可传 `en-US` / `zh-TW`。
     */
    locale?: SiteFooterLocale;
    /** 覆盖当前语言快照中的任意字段（一般不需要） */
    override?: Partial<GcsaFooterSnapshot>;
    /**
     * 显式主题。Web Component 路径必传（经 CE 同步），
     * 用内部 class 驱动样式，避免 iOS 上 `:host([theme])` 不重算。
     */
    theme?: 'dark' | 'light' | '';
  }>(),
  {
    locale: 'zh-CN',
    override: undefined,
    theme: ''
  }
);

const themeClass = computed(() => {
  if (props.theme === 'dark') return 'site-footer-theme--dark';
  if (props.theme === 'light') return 'site-footer-theme--light';
  return '';
});

const activeCounsel = ref<GcsaLegalCounselFirm | null>(null);

const resolved = computed<GcsaFooterSnapshot>(() => {
  const base = resolveGcsaFooter(props.locale);
  if (!props.override) return base;
  return {
    ...base,
    ...props.override,
    navLinks: props.override.navLinks ?? base.navLinks,
    socialLinks: props.override.socialLinks ?? base.socialLinks,
    legalLinks: props.override.legalLinks ?? base.legalLinks
  };
});

const counselCopy = computed(() => resolveGcsaLegalCounsel(props.locale));

const counselButtonLabel = (firm: GcsaLegalCounselFirm) =>
  counselCopy.value.openIntro.replace('{name}', firm.title);

watch(counselCopy, copy => {
  if (!activeCounsel.value) return;
  activeCounsel.value =
    copy.firms.find(firm => firm.id === activeCounsel.value?.id) ?? null;
});
</script>
